import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Input, Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import Avatar from '@shared/ui/Avatar';
import CreatorDetailModal from './CreatorDetailModal';
import SkiplistManager from './SkiplistManager';
import { PaginationBar } from './PendingBrandHandles';
import { SortHeader, THStyle, TDStyle, type SortDir } from './tableSort';
import SuggestedHandleCard from './SuggestedHandleCard';
import BrandTagPostsModal from './BrandTagPostsModal';
import * as bulkStore from './bulkOpsStore';
import { currentLocale } from '@shared/i18n';

type Tab = 'social' | 'suggested' | 'skipped';

interface ProviderRow {
  id: string;
  user_id: string | null;
  name: string | null;
  handle: string | null;
  type: string | null;
  city: string | null;
  instagram_providers?: Array<{ id: string; platform: string | null; handle: string | null }> | null;
  // Social-tab only — populated by the v_admin_ig_creators view.
  instagram_provider_id?: string | null;
  last_post_scrape_at?: string | null;   // ISO date (from ig_profile_snapshots.scraped_date)
  last_profile_scrape_at?: string | null; // ISO timestamp (from instagram_providers.last_profile_scrape_at)
  last_story_scrape_at?: string | null;  // ISO timestamp (from instagram_providers.last_story_scrape_at)
  last_story_count?: number | null;
  posts_total?: number | null;
  stories_total?: number | null;
  followers?: number | null;
  engagement_rate?: number | null;
  avatar_url?: string | null;
  verified?: boolean | null;   // scraped IG blue check (instagram_providers.verified)
  claimed?: boolean | null;    // a real user has verified ownership (creator_claims)
}

export default function CreatorsDiscovery() {
  const { t } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'social';
  const setTab = (k: Tab) => {
    const next = new URLSearchParams(params);
    if (k === 'social') next.delete('tab'); else next.set('tab', k);
    setParams(next, { replace: false });
  };
  const [search, setSearch] = useState('');
  // Suggested-tab-only: combinable tagged-@handle + bio-keyword searches (rendered in the
  // shared search row so all three sit together, matching the Pending IG Handles layout).
  const [tagged, setTagged] = useState('');
  const [bioKw, setBioKw] = useState('');
  const [openSocialAccountId, setOpenSocialAccountId] = useState<string | null>(null);
  const closeCreatorModal = useCallback(() => setOpenSocialAccountId(null), []);

  return (
    <SectionErrorBoundary name="admin-providers">
      <h2 style={{ margin: '0 0 16px' }}>{t('providers.title')}</h2>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid rgba(15,45,100,0.4)' }}>
        {(['social', 'suggested', 'skipped'] as Tab[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => { setTab(k); setSearch(''); setTagged(''); setBioKw(''); }}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === k ? '2px solid #4a9eff' : '2px solid transparent',
              color: tab === k ? '#fff' : '#9aa',
              fontWeight: tab === k ? 600 : 400,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 'var(--fs-body)',
            }}
          >
            {k === 'skipped'
              ? t('providers.tab.skipped', { defaultValue: 'Skipped' })
              : t(`providers.tab.${k}`)}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          placeholder={t('providers.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 200, flex: 1 }}
        />
        {tab === 'suggested' && (
          <>
            <Input
              placeholder={t('providers.suggested.taggedPlaceholder', { defaultValue: 'Tagged @handle (e.g. mbank)…' })}
              value={tagged}
              onChange={(e) => setTagged(e.target.value)}
              style={{ minWidth: 180, flex: 1, borderColor: tagged ? '#4a9eff' : undefined }}
            />
            <Input
              placeholder={t('providers.suggested.bioPlaceholder', { defaultValue: 'Bio keyword (e.g. реклама, UGC, блогер)…' })}
              value={bioKw}
              onChange={(e) => setBioKw(e.target.value)}
              style={{ minWidth: 180, flex: 1, borderColor: bioKw ? '#2ecc71' : undefined }}
            />
          </>
        )}
        {tab === 'social' && <AddCreatorButton />}
      </div>

      {tab === 'social' && (
        <ProviderList
          typeFilter={['smm']}
          search={search}
          onPickSocial={(saId) => setOpenSocialAccountId(saId)}
          deletable
        />
      )}
      {tab === 'suggested' && <SuggestedCreatorsList search={search} tagged={tagged} bioKw={bioKw} />}
      {tab === 'skipped' && <SkiplistManager kind="creator" search={search} />}

      {openSocialAccountId && (
        <CreatorDetailModal
          socialAccountId={openSocialAccountId}
          onClose={closeCreatorModal}
        />
      )}
    </SectionErrorBoundary>
  );
}

function ScrapeBadge({
  label, count, lastAt, t,
}: {
  label: string;
  count: number | null;
  lastAt: string | null | undefined;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (!lastAt) {
    return (
      <span style={{ color: '#666' }}>
        {label}: {t('providers.scrape.never', { defaultValue: 'never' })}
      </span>
    );
  }
  const date = new Date(lastAt);
  const dateStr = isNaN(date.getTime())
    ? lastAt
    : date.toLocaleDateString(currentLocale());
  return (
    <span>
      <span style={{ color: '#9aa' }}>{label}:</span>{' '}
      <span style={{ color: '#bcd0ff' }}>{count != null ? count.toLocaleString() : '?'}</span>
      <span style={{ color: '#666' }}> · {dateStr}</span>
    </span>
  );
}

// ── Cron-health strip ──────────────────────────────────────────────────────
// Shows scrape freshness (from get_scrape_health) + the latest GitHub Actions run
// status (from /api/workflow-status) for the three scheduled jobs.

interface ScrapeHealth {
  total: number;
  profiles_last: string | null;
  posts_last: string | null;
  stories_last: string | null;
  profiles_stale: number;
  stories_stale: number;
}

interface WorkflowRun {
  status: string | null;
  conclusion: string | null;
  created_at: string | null;
  html_url: string | null;
  run_number: number | null;
}

interface WorkflowStatus {
  weekly: WorkflowRun | null;
  daily: WorkflowRun | null;
  video: WorkflowRun | null;
}

type HealthColor = 'green' | 'amber' | 'red';

const HEALTH_COLORS: Record<HealthColor, { fg: string; bg: string; border: string }> = {
  green: { fg: '#5fd17a', bg: 'rgba(95,209,122,0.1)', border: 'rgba(95,209,122,0.3)' },
  amber: { fg: '#f5b700', bg: 'rgba(245,183,0,0.1)', border: 'rgba(245,183,0,0.3)' },
  red: { fg: '#e74c3c', bg: 'rgba(231,76,60,0.1)', border: 'rgba(231,76,60,0.4)' },
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;

// Relative freshness, localized. null → "never".
function relFreshness(iso: string | null, t: TFn): string {
  if (!iso) return t('providers.health.never', { defaultValue: 'никогда' });
  const then = new Date(iso).getTime();
  if (isNaN(then)) return t('providers.health.never', { defaultValue: 'никогда' });
  const diffMs = Date.now() - then;
  if (diffMs < 0) return t('providers.health.justNow', { defaultValue: 'обновлено только что' });
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return t('providers.health.minsAgo', { n: mins, defaultValue: 'обновлено {{n}} мин назад' });
  const hours = Math.floor(mins / 60);
  if (hours < 48) return t('providers.health.hoursAgo', { n: hours, defaultValue: 'обновлено {{n}} ч назад' });
  const days = Math.floor(hours / 24);
  return t('providers.health.daysAgo', { n: days, defaultValue: 'обновлено {{n}} дн назад' });
}

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

// Profiles & Posts: green <8d, amber 8–10d, red >10d or null.
function postsLikeColor(iso: string | null): HealthColor {
  if (!iso) return 'red';
  const age = Date.now() - new Date(iso).getTime();
  if (isNaN(age)) return 'red';
  if (age < 8 * DAY_MS) return 'green';
  if (age <= 10 * DAY_MS) return 'amber';
  return 'red';
}

// Stories: green <36h, amber 36h–3d, red >3d or null.
function storiesColor(iso: string | null): HealthColor {
  if (!iso) return 'red';
  const age = Date.now() - new Date(iso).getTime();
  if (isNaN(age)) return 'red';
  if (age < 36 * HOUR_MS) return 'green';
  if (age <= 3 * DAY_MS) return 'amber';
  return 'red';
}

function WorkflowRunLine({ run }: { run: WorkflowRun | null }) {
  if (!run || !run.status) return null;
  let icon = '⏳';
  let label = 'running';
  if (run.status === 'completed') {
    if (run.conclusion === 'success') { icon = '✅'; label = 'success'; }
    else { icon = '❌'; label = run.conclusion ?? 'failed'; }
  }
  const inner = <>{icon} {label}</>;
  return (
    <div style={{ fontSize: 'var(--fs-micro)', marginTop: 3, color: '#9aa', textAlign: 'center' }}>
      {run.html_url ? (
        <a href={run.html_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
          {inner}
        </a>
      ) : inner}
    </div>
  );
}

function HealthChip({ label, color, freshness, run }: { label: string; color: HealthColor; freshness: string; run: WorkflowRun | null }) {
  const c = HEALTH_COLORS[color];
  return (
    <div style={{
      padding: '6px 12px', borderRadius: 8, fontSize: 'var(--fs-small)',
      background: c.bg, border: `1px solid ${c.border}`, minWidth: 120, textAlign: 'center',
    }}>
      <div style={{ fontWeight: 'var(--fw-semibold)', color: c.fg }}>{label}</div>
      {freshness && <div style={{ fontSize: 'var(--fs-caption)', color: '#9aa', marginTop: 2 }}>{freshness}</div>}
      <WorkflowRunLine run={run} />
    </div>
  );
}

function CronHealthStrip() {
  const { t } = useTranslation('admin');
  const [health, setHealth] = useState<ScrapeHealth | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc('get_scrape_health');
        if (!cancelled && data) setHealth(data as ScrapeHealth);
      } catch { /* ignore */ }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const resp = await fetch('/api/workflow-status', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const json = await resp.json() as WorkflowStatus & { error?: string };
        if (!cancelled && resp.ok && !json.error) {
          setWorkflows({ weekly: json.weekly ?? null, daily: json.daily ?? null, video: json.video ?? null });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!health) return null;

  const profilesColor = postsLikeColor(health.profiles_last);
  const postsColor = postsLikeColor(health.posts_last);
  const storiesCol = storiesColor(health.stories_last);
  const anyRed = profilesColor === 'red' || postsColor === 'red' || storiesCol === 'red';

  return (
    <div style={{ marginBottom: 12 }}>
      {anyRed && (
        <div style={{
          marginBottom: 8, padding: '8px 12px', borderRadius: 8, fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)',
          background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.4)', color: '#f08a8a',
        }}>
          {t('providers.health.staleWarning', { defaultValue: '⚠️ Похоже, плановый скрап не обновлял данные — проверьте GitHub Actions' })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <HealthChip label={t('providers.health.profiles', { defaultValue: 'Профили' })} color={profilesColor} freshness={relFreshness(health.profiles_last, t)} run={workflows?.weekly ?? null} />
        <HealthChip label={t('providers.health.posts', { defaultValue: 'Посты' })} color={postsColor} freshness={relFreshness(health.posts_last, t)} run={workflows?.weekly ?? null} />
        <HealthChip label={t('providers.health.stories', { defaultValue: 'Сторис' })} color={storiesCol} freshness={relFreshness(health.stories_last, t)} run={workflows?.daily ?? null} />
        {workflows?.video && (
          <HealthChip label={t('providers.health.video', { defaultValue: 'Видео' })} color="green" freshness="" run={workflows.video} />
        )}
      </div>
    </div>
  );
}

function ProviderList({
  typeFilter,
  search,
  onPickSocial,
  deletable,
}: {
  typeFilter: string[];
  search: string;
  onPickSocial?: (socialAccountId: string) => void;
  deletable?: boolean;
}) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState<string | null>(null);
  const { data, isLoading, error } = useSupabaseQuery<ProviderRow[]>(
    ['admin-providers', typeFilter.join(','), search, onPickSocial ? 'with-social' : 'no-social'],
    async () => {
      // Bloggers list via the v_admin_ig_creators view (derived scrape-status columns).
      // (`providers` was dissolved 2026-07-18 — creators live in instagram_providers.)
      let q = supabase
        .from('v_admin_ig_creators')
        .select('id, user_id, name, handle, type, city, instagram_provider_id, last_post_scrape_at, last_profile_scrape_at, last_story_scrape_at, last_story_count, posts_total, stories_total, followers, engagement_rate, avatar_url, verified, claimed')
        .order('name', { ascending: true })
        .limit(500);
      if (search.trim()) {
        // PostgREST ilike wildcard is `*` in URLs (round-trips better than `%`).
        const term = search.trim().replace(/[*,()]/g, '');
        q = q.or(`name.ilike.*${term}*,handle.ilike.*${term}*`);
      }
      return q;
    },
  );

  const [deleteTarget, setDeleteTarget] = useState<ProviderRow | null>(null);

  if (isLoading) return <Skeleton height={240} />;
  if (error) return <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>;
  const rows = data ?? [];
  if (rows.length === 0) return <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>{t('providers.empty')}</div>;

  // Social tab uses a column-aligned table with a totals footer; other callers
  // keep the existing card list.
  if (onPickSocial) {
    return (
      <CreatorsTable
        rows={rows}
        deletable={!!deletable}
        deleting={deleting}
        onPick={onPickSocial}
        onDeleteRequest={(p) => setDeleteTarget(p)}
        deleteTarget={deleteTarget}
        onDeleteClose={() => setDeleteTarget(null)}
        onDeleteDone={() => {
          setDeleteTarget(null);
          qc.invalidateQueries({ queryKey: ['admin-providers'] });
          qc.invalidateQueries({ queryKey: ['admin-recommended-creators'] });
          qc.invalidateQueries({ queryKey: ['admin-recommended-creators-breakdown'] });
        }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((p) => {
        const isDeleting = deleting === p.id;
        return (
          <div
            key={p.id}
            style={{
              padding: 12,
              borderRadius: 8,
              border: '1px solid rgba(15,45,100,0.4)',
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              opacity: isDeleting ? 0.5 : 1,
            }}
          >
            <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar src={p.avatar_url} name={p.handle ?? p.name ?? '?'} size={34} />
              <div>
                <div style={{ fontWeight: 'var(--fw-semibold)' }}>{p.name ?? '—'}</div>
                <div style={{ fontSize: 'var(--fs-small)', color: '#888' }}>
                  {p.handle ?? '—'}{p.type ? ` · ${p.type}` : ''}{p.city ? ` · ${p.city}` : ''}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreatorsTable({
  rows, deletable, deleting, onPick, onDeleteRequest, deleteTarget, onDeleteClose, onDeleteDone,
}: {
  rows: ProviderRow[];
  deletable: boolean;
  deleting: string | null;
  onPick: (socialAccountId: string) => void;
  onDeleteRequest: (p: ProviderRow) => void;
  deleteTarget: ProviderRow | null;
  onDeleteClose: () => void;
  onDeleteDone: () => void;
}) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  // Subscribe to module-level store so bulk progress survives tab switches
  const storeState = useSyncExternalStore(bulkStore.subscribe, bulkStore.getState, bulkStore.getState);
  const scraping = storeState.scraping;
  const mirroringVideo = storeState.mirroringVideo;
  const bulkProgress = storeState.bulkProgress;
  const bulkVideoProgress = storeState.bulkVideoProgress;
  const bulkCarouselProgress = storeState.bulkCarouselProgress;
  const bulkThumbProgress = storeState.bulkThumbProgress;
  const importProgress = storeState.importProgress;
  const importing = storeState.importing;

  // Setters wrap the store
  const setScraping = (v: string | null) => bulkStore.setState({ scraping: v });
  const setMirroringVideo = (v: string | null) => bulkStore.setState({ mirroringVideo: v });
  const setBulkProgress = (v: bulkStore.BulkProgress) => bulkStore.setState({ bulkProgress: v });
  const setBulkVideoProgress = (v: bulkStore.BulkVideoProgress) => bulkStore.setState({ bulkVideoProgress: v });
  const setBulkCarouselProgress = (v: bulkStore.BulkCarouselProgress) => bulkStore.setState({ bulkCarouselProgress: v });
  const setBulkThumbProgress = (v: bulkStore.BulkThumbProgress) => bulkStore.setState({ bulkThumbProgress: v });
  const setImportProgress = (v: bulkStore.ImportProgress) => bulkStore.setState({ importProgress: v });
  const setImporting = (v: boolean) => bulkStore.setState({ importing: v });

  // These three are cosmetic toasts — local state is fine; they don't represent in-flight work
  const [scrapeResult, setScrapeResult] = useState<{ handle: string; inserted: number; updated?: number; apifyItems?: number; rowsBuilt?: number; cost: string } | null>(null);
  const [videoResult, setVideoResult] = useState<{ handle: string; mirrored: number; skipped: number } | null>(null);
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const bulkAbortRef = useRef(false);
  const importAbortRef = useRef(false);
  const bulkCarouselAbortRef = useRef(false);
  const bulkThumbAbortRef = useRef(false);
  const bulkStoryAbortRef = useRef(false);
  // Story scrape progress (local — the story actor is batch so this loop is short-lived)
  const [bulkStoryProgress, setBulkStoryProgress] = useState<{ current: number; total: number; stories: number; errors: number } | null>(null);
  // Story-run import (free re-import of already-completed story runs)
  // Vide bulk has its own ref but was already declared earlier — leave as-is
  const bulkVideoAbortRef = useRef(false);

  // Only the bloggers that actually have RECOVERABLE pending mirrors of this kind
  // (skips fully-done bloggers AND dead-URL-only ones via the oe-aware RPC) — so the
  // loop visits e.g. 13 instead of 0→418 every run.
  const pendingMirrorBloggers = async (kind: 'thumb' | 'carousel' | 'video'): Promise<Array<{ handle: string }>> => {
    const { data } = await supabase.rpc('providers_with_pending_mirror', { p_kind: kind });
    return (data ?? []).map((r: { handle: string }) => ({ handle: r.handle })).filter((b: { handle: string }) => !!b.handle);
  };

  const mirrorAllThumbs = async () => {
    const bloggers = await pendingMirrorBloggers('thumb');
    if (!bloggers.length) return;
    bulkThumbAbortRef.current = false;
    setScrapeError(null);
    setBulkThumbProgress({ current: 0, total: bloggers.length, handle: '', mirrored: 0 });

    let totalMirrored = 0;
    for (let i = 0; i < bloggers.length; i++) {
      if (bulkThumbAbortRef.current) break;
      const { handle } = bloggers[i];
      setBulkThumbProgress({ current: i + 1, total: bloggers.length, handle, mirrored: totalMirrored });
      let pending = true;
      while (pending && !bulkThumbAbortRef.current) {
        const tok = await getFreshToken();
        if (!tok) { setScrapeError('Session expired'); pending = false; break; }
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 320_000);
        try {
          const resp = await fetch('/api/mirror-thumbs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ handle }),
            signal: controller.signal,
          });
          const json = await resp.json() as { mirrored?: number; pending?: boolean; error?: string };
          if (!resp.ok || json.error) throw new Error(json.error ?? 'failed');
          totalMirrored += json.mirrored ?? 0;
          setBulkThumbProgress({ current: i + 1, total: bloggers.length, handle, mirrored: totalMirrored });
          pending = !!json.pending;
        } catch {
          pending = false;
        } finally {
          clearTimeout(timeoutId);
        }
      }
    }
    setBulkThumbProgress(null);
    setScrapeResult({ handle: 'all-thumbs', inserted: totalMirrored, cost: '0.0000' });
    qc.invalidateQueries({ queryKey: ['admin-providers'] });
  };

  const mirrorAllCarousels = async () => {
    const bloggers = await pendingMirrorBloggers('carousel');
    if (!bloggers.length) return;
    bulkCarouselAbortRef.current = false;
    setScrapeError(null);
    setBulkCarouselProgress({ current: 0, total: bloggers.length, handle: '', slides: 0 });

    let totalSlides = 0;
    for (let i = 0; i < bloggers.length; i++) {
      if (bulkCarouselAbortRef.current) break;
      const { handle } = bloggers[i];
      setBulkCarouselProgress({ current: i + 1, total: bloggers.length, handle, slides: totalSlides });
      let pending = true;
      while (pending && !bulkCarouselAbortRef.current) {
        const tok = await getFreshToken();
        if (!tok) { setScrapeError('Session expired'); pending = false; break; }
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 320_000);
        try {
          const resp = await fetch('/api/mirror-carousel', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ handle }),
            signal: controller.signal,
          });
          const json = await resp.json() as { slides_total?: number; pending?: boolean; error?: string };
          if (!resp.ok || json.error) throw new Error(json.error ?? 'failed');
          totalSlides += json.slides_total ?? 0;
          setBulkCarouselProgress({ current: i + 1, total: bloggers.length, handle, slides: totalSlides });
          pending = !!json.pending;
        } catch {
          pending = false; // move on to next blogger
        } finally {
          clearTimeout(timeoutId);
        }
      }
    }
    setBulkCarouselProgress(null);
    setScrapeResult({ handle: 'all-carousel', inserted: totalSlides, cost: '0.0000' });
    qc.invalidateQueries({ queryKey: ['admin-providers'] });
  };

  const importApifyRuns = async () => {
    setImporting(true);
    setImportProgress({ runs: 0, total: 0, inserted: 0, updated: 0 });
    setScrapeError(null);
    setScrapeResult(null);
    importAbortRef.current = false;

    let offset = 0;
    let totalIns = 0;
    let totalUpd = 0;
    let totalRunsSeen = 0;
    let totalRuns = 0;

    try {
      while (!importAbortRef.current) {
        const tok = await getFreshToken();
        if (!tok) throw new Error('Session expired');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 320_000);
        const resp = await fetch('/api/import-apify-runs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ offset, limit: 30 }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timeoutId));
        const json = await resp.json() as {
          runs_seen?: number; inserted?: number; updated?: number; total_runs?: number;
          next_offset?: number; more?: boolean; error?: string;
        };
        if (!resp.ok || json.error) throw new Error(json.error ?? 'import failed');

        totalIns += json.inserted ?? 0;
        totalUpd += json.updated ?? 0;
        totalRunsSeen += json.runs_seen ?? 0;
        totalRuns = json.total_runs ?? totalRuns;
        offset = json.next_offset ?? offset + 30;

        setImportProgress({ runs: totalRunsSeen, total: totalRuns, inserted: totalIns, updated: totalUpd });

        if (!json.more) break;
      }
      setScrapeResult({ handle: 'all', inserted: totalIns, updated: totalUpd, cost: '0.0000' });
    } catch (e) {
      setScrapeError((e as Error).message);
    } finally {
      setImporting(false);
      setImportProgress(null);
      qc.invalidateQueries({ queryKey: ['admin-providers'] });
    }
  };

  const triggerScrape = async (handle: string, type: 'posts' | 'reels' | 'both', providerId: string) => {
    setScraping(providerId);
    setScrapeResult(null);
    setScrapeError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const resp = await fetch('/api/scrape-blogger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ handle, type }),
      });
      const json = await resp.json() as { inserted?: number; updated?: number; apify_items?: number; rows_built?: number; cost?: string; error?: string; message?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Scrape failed');
      setScrapeResult({ handle, inserted: json.inserted ?? 0, updated: json.updated ?? 0, apifyItems: json.apify_items, rowsBuilt: json.rows_built, cost: json.cost ?? '0' });
      qc.invalidateQueries({ queryKey: ['admin-providers'] });
    } catch (e) {
      setScrapeError((e as Error).message);
    } finally {
      setScraping(null);
    }
  };

  // Video mirroring runs on GitHub Actions (brand-only + 480p transcode) instead of
  // the old per-handle full-res /api/mirror-videos path. Both video buttons just fire
  // the workflow_dispatch via /api/trigger-video-mirror (admin-gated).
  const triggerVideoWorkflow = async () => {
    setVideoResult(null);
    setScrapeError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const resp = await fetch('/api/trigger-video-mirror', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ limit: 200 }),
      });
      const json = await resp.json() as { ok?: boolean; actions_url?: string; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Failed to start workflow');
      window.alert('🎬 Brand-video mirror started on GitHub Actions (brand-only, 480p).\n\nTrack progress:\n' + (json.actions_url ?? ''));
    } catch (e) {
      setScrapeError((e as Error).message);
    }
  };

  const triggerMirrorVideos = async (_handle: string, _providerId: string) => {
    await triggerVideoWorkflow();
  };

  const mirrorAllVideos = async () => {
    await triggerVideoWorkflow();
  };

  // Refresh the session so a long-running bulk loop doesn't fail mid-way
  // when the access_token (1h default) expires. Returns a fresh access_token.
  const getFreshToken = async (): Promise<string | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    // If token expires within 5 minutes, force a refresh
    const expiresAt = (session.expires_at ?? 0) * 1000;
    if (expiresAt - Date.now() < 5 * 60 * 1000) {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) return session.access_token;
      return data.session.access_token;
    }
    return session.access_token;
  };

  const runBulkScrape = async (bloggers: Array<{ handle: string; saId: string }>, sinceDateOverride?: string | null) => {

    if (!bloggers.length) return;
    bulkAbortRef.current = false;
    setScrapeResult(null);
    setScrapeError(null);
    setBulkProgress({ current: 0, total: bloggers.length, handle: '', inserted: 0, errors: 0 });

    let totalInserted = 0;
    let totalErrors = 0;
    const failedHandles: string[] = [];
    const cappedHandles: string[] = [];
    for (let i = 0; i < bloggers.length; i++) {
      if (bulkAbortRef.current) break;
      const { handle, saId } = bloggers[i];
      setBulkProgress({ current: i + 1, total: bloggers.length, handle, inserted: totalInserted, errors: totalErrors });
      setScraping(saId);
      const tok = await getFreshToken();
      if (!tok) { setScrapeError('Session expired'); setScraping(null); break; }
      // Vercel's maxDuration is 300s. If the connection drops without a clean
      // response (silent infra failure), fetch() waits forever and the loop
      // hangs. AbortController with 320s timeout ensures the loop recovers.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 320_000);
      try {
        // type='posts' (not 'both') — Apify's posts feed includes most reels
        // (productType='clips'). Cuts cost ~50%. Edge case: reels with
        // "Don't share to feed" enabled are missed; per-row Posts button can
        // still trigger 'both' on demand.
        const body: Record<string, unknown> = { handle, type: 'posts' };
        if (sinceDateOverride !== undefined) body.sinceDateOverride = sinceDateOverride;
        const resp = await fetch('/api/scrape-blogger', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        const json = await resp.json() as { inserted?: number; updated?: number; capped?: boolean; error?: string };
        if (!resp.ok || json.error) throw new Error(json.error ?? 'failed');
        totalInserted += json.inserted ?? 0;
        if (json.capped) cappedHandles.push(handle);
        setBulkProgress({ current: i + 1, total: bloggers.length, handle, inserted: totalInserted, errors: totalErrors });
      } catch {
        totalErrors++;
        failedHandles.push(handle);
        setBulkProgress({ current: i + 1, total: bloggers.length, handle, inserted: totalInserted, errors: totalErrors });
      } finally {
        clearTimeout(timeoutId);
        setScraping(null);
      }

      // Short random delay between bloggers to avoid Instagram rate-limiting
      if (i < bloggers.length - 1 && !bulkAbortRef.current) {
        const delaySec = 5 + Math.floor(Math.random() * 6); // 5–10s
        await new Promise((r) => setTimeout(r, delaySec * 1000));
      }
    }
    setBulkProgress(null);
    const parts: string[] = [];
    if (failedHandles.length) {
      parts.push(`${failedHandles.length} failed: @${failedHandles.slice(0, 5).join(', @')}${failedHandles.length > 5 ? `… +${failedHandles.length - 5} more` : ''}`);
    }
    if (cappedHandles.length) {
      parts.push(`${cappedHandles.length} capped (need wider re-scrape — use "all (full backfill)"): @${cappedHandles.slice(0, 5).join(', @')}${cappedHandles.length > 5 ? `… +${cappedHandles.length - 5} more` : ''}`);
    }
    if (parts.length) setScrapeError(parts.join(' · '));
    setScrapeResult({ handle: 'all', inserted: totalInserted, cost: (totalInserted * 0.0027).toFixed(4) });
    qc.invalidateQueries({ queryKey: ['admin-providers'] });
  };

  // Free re-import of already-completed story runs (no new Apify tokens) — mirrors importApifyRuns.
  // Replay already-paid story runs. Runs on GitHub Actions, not here: one story run
  // carries ~1000 items with full raw_json, which a 300s serverless function cannot
  // finish — the old in-browser loop silently reported "0 imported" instead.
  const importStoryRuns = async () => {
    setScrapeResult(null);
    setScrapeError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const resp = await fetch('/api/trigger-workflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workflow: 'daily', inputs: { import_runs: 'auto:10' } }),
      });
      const json = await resp.json() as { ok?: boolean; actions_url?: string; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Failed to start workflow');
      window.alert('Импорт сторис запущен на GitHub Actions (бесплатно, без новых запусков Apify).\n' + (json.actions_url ?? ''));
    } catch (e) {
      setScrapeError((e as Error).message);
    }
  };

  const toBloggerList = (filter?: (p: ProviderRow) => boolean) =>
    rows
      .filter(filter ?? (() => true))
      .map((p) => ({
        handle: p.handle?.replace(/^@+/, '') ?? null,
        saId: p.instagram_provider_id
          ?? (p.instagram_providers ?? []).find((sa) => sa?.platform === 'Instagram')?.id
          ?? null,
      }))
      .filter((b): b is { handle: string; saId: string } => !!b.handle && !!b.saId);

  // Optional override — when set, scrape from this date instead of incremental.
  // 'YYYY-MM-DD' string, or null = full backfill (no filter), or undefined = use default incremental.
  const [sinceDateInput, setSinceDateInput] = useState<string>('');
  const sinceDateOverrideArg: string | null | undefined =
    sinceDateInput === '' ? undefined : (sinceDateInput === 'all' ? null : sinceDateInput);

  // Skip bloggers whose last_post_scrape_at is within N days (0 = no skip).
  const [skipRecentDays, setSkipRecentDays] = useState<number>(7);

  const recentCutoff = skipRecentDays > 0 ? new Date(Date.now() - skipRecentDays * 86_400_000) : null;
  const notRecentlyScraped = (p: ProviderRow) => {
    if (!recentCutoff) return true;
    if (!p.last_post_scrape_at) return true;
    return new Date(p.last_post_scrape_at) < recentCutoff;
  };

  // Bulk profile+post scrape now runs on GitHub Actions (the 'weekly' workflow)
  // instead of the browser-driven runBulkScrape loop. Fires workflow_dispatch via
  // /api/trigger-workflow (admin-gated) — same shape as triggerVideoWorkflow.
  const scrapeAll = async () => {
    setScrapeResult(null);
    setScrapeError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const resp = await fetch('/api/trigger-workflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workflow: 'weekly' }),
      });
      const json = await resp.json() as { ok?: boolean; actions_url?: string; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Failed to start workflow');
      window.alert('Запущен плановый скрап (профили+посты) на GitHub Actions.\n' + (json.actions_url ?? ''));
    } catch (e) {
      setScrapeError((e as Error).message);
    }
  };
  // Scrape Missing now uses the SAME profile pipeline as the weekly job (Apify
  // profile actor + gap-fill + inline mirror), scoped to exactly the 0-post handles,
  // via a targeted GitHub workflow_dispatch (handles input). No more old per-handle actor.
  const scrapeMissing = async () => {
    setScrapeError(null);
    const missing = toBloggerList((p) => !p.posts_total || p.posts_total === 0);
    if (!missing.length) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const resp = await fetch('/api/trigger-workflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          workflow: 'weekly',
          inputs: { handles: missing.map((m) => m.handle).join(','), gap_fill: 'true', skip_recent_days: '0' },
        }),
      });
      const json = await resp.json() as { ok?: boolean; actions_url?: string; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Failed to start workflow');
      window.alert(`Запущен скрап профилей для ${missing.length} блогеров без постов на GitHub Actions.\n` + (json.actions_url ?? ''));
    } catch (e) {
      setScrapeError((e as Error).message);
    }
  };

  // Stories: gated by last_story_scrape_at (independent of post-scrape recency).
  const notRecentlyStoryScraped = (p: ProviderRow) => {
    if (!recentCutoff) return true;
    if (!p.last_story_scrape_at) return true;
    return new Date(p.last_story_scrape_at) < recentCutoff;
  };

  // The story actor is natively batch: one run can take many usernames
  // and returns all their active stories interleaved. Billing is PER PROFILE
  // ($0.005/profile + $0.01/run start) — NOT per story — so a full sweep is ~cents and
  // cost is driven by how many handles × how often we scrape, not story volume. We send
  // large CHUNKS for fewer runs. If a big run outlasts the 270s live-poll window, the
  // Apify run still completes and "📚 Import Stories (free)" recovers it — risk-free.
  const STORY_CHUNK = 25;
  // Bulk story scrape now runs on GitHub Actions (the 'daily' workflow) instead of
  // the browser-driven chunked loop. Fires workflow_dispatch via /api/trigger-workflow.
  const scrapeAllStories = async () => {
    setScrapeResult(null);
    setScrapeError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const resp = await fetch('/api/trigger-workflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ workflow: 'daily' }),
      });
      const json = await resp.json() as { ok?: boolean; actions_url?: string; error?: string };
      if (!resp.ok || json.error) throw new Error(json.error ?? 'Failed to start workflow');
      window.alert('Запущен скрап сторис на GitHub Actions.\n' + (json.actions_url ?? ''));
    } catch (e) {
      setScrapeError((e as Error).message);
    }
  };

  // Footer totals: simple sums + average for engagement (weighted by followers
  // would be more correct but doesn't matter at admin-list scale).
  const totals = rows.reduce(
    (acc, p) => {
      acc.followers += p.followers ?? 0;
      acc.posts += p.posts_total ?? 0;
      acc.stories += p.stories_total ?? 0;
      if (p.engagement_rate != null) {
        acc.erSum += Number(p.engagement_rate);
        acc.erN += 1;
      }
      return acc;
    },
    { followers: 0, posts: 0, stories: 0, erSum: 0, erN: 0 },
  );
  const erAvg = totals.erN ? totals.erSum / totals.erN : null;

  const cols: Array<{ key: string; label: string; align?: 'left' | 'right'; width?: string }> = [
    { key: 'creator', label: t('providers.col.creator', { defaultValue: 'Creator' }), align: 'left', width: 'minmax(220px, 2fr)' },
    { key: 'followers', label: t('providers.col.followers', { defaultValue: 'Followers' }), align: 'right', width: 'minmax(110px, 1fr)' },
    { key: 'er', label: t('providers.col.er', { defaultValue: 'ER %' }), align: 'right', width: 'minmax(80px, 1fr)' },
    { key: 'posts', label: t('providers.col.posts', { defaultValue: 'Posts' }), align: 'right', width: 'minmax(120px, 1fr)' },
    { key: 'lastPost', label: t('providers.col.lastPost', { defaultValue: 'Last post scrape' }), align: 'right', width: 'minmax(140px, 1fr)' },
    { key: 'lastProfile', label: t('providers.col.lastProfile', { defaultValue: 'Last profile refresh' }), align: 'right', width: 'minmax(140px, 1fr)' },
    { key: 'stories', label: t('providers.col.stories', { defaultValue: 'Stories' }), align: 'right', width: 'minmax(120px, 1fr)' },
    { key: 'lastStory', label: t('providers.col.lastStory', { defaultValue: 'Last story scrape' }), align: 'right', width: 'minmax(140px, 1fr)' },
  ];
  cols.push({ key: 'scrape', label: 'Scrape', align: 'right', width: 'minmax(180px, auto)' });
  if (deletable) {
    cols.push({ key: 'actions', label: '', align: 'right', width: 'minmax(96px, auto)' });
  }
  const gridTemplate = cols.map((c) => c.width ?? '1fr').join(' ');

  const fmtDate = (iso: string | null | undefined) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString(currentLocale());
  };

  return (
    <div>
      <CronHealthStrip />
      {/* legacy skip-days + scrape-from controls removed — scraping is scheduled (GitHub) */}
      {/* Bulk scrape controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {/* Post + story scraping run automatically on cron (weekly / daily GitHub workflows).
            Manual full-fleet triggers removed — the health strip above shows freshness + run
            status, Scrape Missing (Advanced) handles targeted gaps, and the per-row 📸 Posts
            button covers ad-hoc single scrapes. */}
        {/* Video mirror all */}
        <button
          type="button"
          onClick={mirrorAllVideos}
          title="Fires the GitHub Actions 'Mirror brand videos' job: brand-detected reels only, transcoded to 480p"
          style={{
            padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            background: 'rgba(192,132,252,0.08)',
            color: '#a78bfa',
            border: '1px solid rgba(192,132,252,0.25)',
          }}
        >
          🎬 Mirror Brand Videos (GitHub)
        </button>
        {bulkVideoProgress && (
          <button
            type="button"
            onClick={() => { bulkVideoAbortRef.current = true; }}
            style={{ padding: '5px 10px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.4)' }}
          >
            Stop
          </button>
        )}
        {/* Advanced / maintenance backstops (mirroring is automatic in the scheduled jobs) */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          title="Backstop & maintenance actions (mirroring runs automatically in the scheduled jobs)"
          style={{ padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#9aa', border: '1px dashed rgba(255,255,255,0.18)' }}
        >
          {showAdvanced ? '🛠 Advanced ▴' : '🛠 Advanced ▾'}
        </button>
        {showAdvanced && (<>
        {/* Scrape missing (0-post bloggers) — backstop; weekly job covers this automatically */}
        {!bulkProgress && (() => {
          const missing = toBloggerList((p) => !p.posts_total || p.posts_total === 0);
          return missing.length > 0 ? (
            <button
              type="button"
              disabled={!!scraping}
              onClick={scrapeMissing}
              title="Re-scrape bloggers with 0 posts (incremental)"
              style={{
                padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                background: 'rgba(245,183,0,0.08)', color: '#f5b700',
                border: '1px solid rgba(245,183,0,0.3)',
                opacity: !!scraping ? 0.6 : 1,
              }}
            >
              ⚠️ Scrape Missing ({missing.length})
            </button>
          ) : null;
        })()}
        {/* Mirror thumbnails (free) */}
        <button
          type="button"
          disabled={!!bulkThumbProgress}
          onClick={mirrorAllThumbs}
          title="Mirror cover thumbnails to Supabase storage (free)"
          style={{
            padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            background: bulkThumbProgress ? 'rgba(74,158,255,0.1)' : 'rgba(74,158,255,0.05)',
            color: bulkThumbProgress ? '#bcd0ff' : '#7ab0e0',
            border: '1px solid rgba(74,158,255,0.25)',
            opacity: !!bulkThumbProgress ? 0.7 : 1,
          }}
        >
          {bulkThumbProgress
            ? `⟳ ${bulkThumbProgress.current}/${bulkThumbProgress.total} @${bulkThumbProgress.handle} · ${bulkThumbProgress.mirrored} thumbs…`
            : '🖼️ Mirror All Thumbnails'}
        </button>
        {bulkThumbProgress && (
          <button
            type="button"
            onClick={() => { bulkThumbAbortRef.current = true; }}
            style={{ padding: '5px 10px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.4)' }}
          >
            Stop
          </button>
        )}
        {/* Mirror carousel slides (free) */}
        <button
          type="button"
          disabled={!!bulkCarouselProgress}
          onClick={mirrorAllCarousels}
          title="Mirror carousel slide images to Supabase storage (free)"
          style={{
            padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            background: bulkCarouselProgress ? 'rgba(192,132,252,0.1)' : 'rgba(192,132,252,0.05)',
            color: bulkCarouselProgress ? '#c084fc' : '#a78bfa',
            border: '1px solid rgba(192,132,252,0.25)',
            opacity: !!bulkCarouselProgress ? 0.7 : 1,
          }}
        >
          {bulkCarouselProgress
            ? `⟳ ${bulkCarouselProgress.current}/${bulkCarouselProgress.total} @${bulkCarouselProgress.handle} · ${bulkCarouselProgress.slides} slides…`
            : '🖼 Mirror All Carousels'}
        </button>
        {bulkCarouselProgress && (
          <button
            type="button"
            onClick={() => { bulkCarouselAbortRef.current = true; }}
            style={{ padding: '5px 10px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.4)' }}
          >
            Stop
          </button>
        )}
        {/* Import existing Apify runs (no new tokens consumed) */}
        <button
          type="button"
          disabled={importing}
          onClick={importApifyRuns}
          title="Re-import already-scraped data from Apify (free, datasets persist 7 days)"
          style={{
            padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            background: importing ? 'rgba(95,209,122,0.1)' : 'rgba(95,209,122,0.08)',
            color: importing ? '#5fd17a' : '#5fd17a',
            border: '1px solid rgba(95,209,122,0.3)',
            opacity: importing ? 0.7 : 1,
          }}
        >
          {importProgress
            ? `⟳ ${importProgress.runs}/${importProgress.total} runs · ${importProgress.inserted} new + ${importProgress.updated} upd…`
            : '♻️ Import Apify Data (free)'}
        </button>
        {importing && (
          <button
            type="button"
            onClick={() => { importAbortRef.current = true; }}
            style={{ padding: '5px 10px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.4)' }}
          >
            Stop
          </button>
        )}
        {/* Import existing story runs — dispatched to GitHub Actions (no new Apify runs) */}
        <button
          type="button"
          onClick={importStoryRuns}
          title="Replay already-completed story runs from Apify into ig_stories on GitHub Actions (free; datasets persist 7 days)"
          style={{
            padding: '5px 14px', fontSize: 'var(--fs-small)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            background: 'rgba(255,159,67,0.06)', color: '#e8954a', border: '1px solid rgba(255,159,67,0.3)',
          }}
        >
          📚 Import Stories (free)
        </button>
        </>)}
      </div>

      {/* Result toasts */}
      {(scrapeResult || videoResult || scrapeError) && (
        <div style={{
          marginBottom: 12, padding: '10px 14px', borderRadius: 8, fontSize: 'var(--fs-body)',
          background: scrapeError ? 'rgba(231,76,60,0.08)' : 'rgba(95,209,122,0.08)',
          border: `1px solid ${scrapeError ? 'rgba(231,76,60,0.3)' : 'rgba(95,209,122,0.3)'}`,
          color: scrapeError ? '#f08a8a' : '#5fd17a',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span>
            {scrapeError
              ? `❌ ${scrapeError}`
              : scrapeResult
                ? (scrapeResult.handle === 'all'
                    ? `✅ All bloggers: ${scrapeResult.inserted} total posts scraped · est. $${scrapeResult.cost}`
                    : scrapeResult.handle === 'all-carousel'
                      ? `✅ All bloggers: ${scrapeResult.inserted} carousel slides mirrored`
                      : scrapeResult.handle === 'all-thumbs'
                        ? `✅ All bloggers: ${scrapeResult.inserted} thumbnails mirrored`
                      : `✅ @${scrapeResult.handle}: ${scrapeResult.inserted} new · ${scrapeResult.updated ?? 0} updated${scrapeResult.apifyItems != null ? ` (Apify: ${scrapeResult.apifyItems} → saved: ${scrapeResult.rowsBuilt ?? 0})` : ''} · est. $${scrapeResult.cost}`)
                : videoResult!.handle === 'all'
                  ? `✅ All bloggers: ${videoResult!.mirrored} videos mirrored`
                  : `✅ @${videoResult!.handle}: ${videoResult!.mirrored} videos mirrored · ${videoResult!.skipped} skipped (too large)`}
          </span>
          <button onClick={() => { setScrapeResult(null); setVideoResult(null); setScrapeError(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 'var(--fs-label)' }}>×</button>
        </div>
      )}
    <div style={{ overflowX: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8 }}>
      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: gridTemplate,
        padding: '10px 14px', gap: 12,
        background: 'rgba(15,45,100,0.18)',
        fontSize: 'var(--fs-caption)', color: '#9aa', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 'var(--fw-semibold)',
        position: 'sticky', top: 0, zIndex: 1,
      }}>
        {cols.map((c) => (
          <div key={c.key} style={{ textAlign: c.align }}>{c.label}</div>
        ))}
      </div>

      {/* Body */}
      {rows.map((p) => {
        const saId = p.instagram_provider_id
          ?? (p.instagram_providers ?? []).find((sa) => sa?.platform === 'Instagram')?.id
          ?? null;
        const clickable = !!saId;
        const isDeleting = deleting === p.id;
        return (
          <div
            key={p.id}
            onClick={clickable ? () => onPick(saId!) : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onPick(saId!); } : undefined}
            style={{
              display: 'grid', gridTemplateColumns: gridTemplate,
              padding: '10px 14px', gap: 12, alignItems: 'center',
              borderTop: '1px solid rgba(15,45,100,0.3)',
              cursor: clickable ? 'pointer' : 'default',
              fontSize: 'var(--fs-body)',
              opacity: isDeleting ? 0.5 : 1,
              transition: 'background 0.12s',
            }}
            onMouseEnter={clickable ? (e) => (e.currentTarget.style.background = 'rgba(15,45,100,0.12)') : undefined}
            onMouseLeave={clickable ? (e) => (e.currentTarget.style.background = '') : undefined}
          >
            {/* Creator */}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                <span style={{ fontWeight: 'var(--fw-semibold)', color: '#f0f0f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name ?? '—'}
                </span>
                <VerifiedBadge verified={p.verified} claimed={p.claimed} />
              </div>
              <div style={{ fontSize: 'var(--fs-caption)', color: '#888' }}>
                {p.handle ? (
                  <a
                    href={`https://www.instagram.com/${p.handle.replace(/^@+/, '')}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title={t('providers.openHandle', { defaultValue: 'Open on Instagram' })}
                    style={{ color: '#bcd0ff', textDecoration: 'none' }}
                  >
                    {p.handle}
                  </a>
                ) : '—'}
              </div>
            </div>

            {/* Followers */}
            <div style={{ textAlign: 'right', color: '#dde' }}>
              {p.followers != null ? p.followers.toLocaleString() : '—'}
            </div>

            {/* ER */}
            <div style={{ textAlign: 'right', color: '#dde' }}>
              {p.engagement_rate != null ? `${Number(p.engagement_rate).toFixed(2)}%` : '—'}
            </div>

            {/* Posts (count) */}
            <div style={{ textAlign: 'right', color: '#dde' }}>
              {p.posts_total != null ? p.posts_total.toLocaleString() : '—'}
            </div>

            {/* Last post scrape */}
            <div style={{ textAlign: 'right', color: '#9aa', fontSize: 'var(--fs-small)' }}>
              {fmtDate(p.last_post_scrape_at) ?? <span style={{ color: '#666' }}>{t('providers.scrape.never', { defaultValue: 'never' })}</span>}
            </div>

            {/* Last profile refresh */}
            <div style={{ textAlign: 'right', color: '#9aa', fontSize: 'var(--fs-small)' }}>
              {fmtDate(p.last_profile_scrape_at) ?? <span style={{ color: '#666' }}>{t('providers.scrape.never', { defaultValue: 'never' })}</span>}
            </div>

            {/* Stories total (with last batch in subscript) */}
            <div style={{ textAlign: 'right', color: '#dde' }}>
              {p.stories_total != null ? p.stories_total.toLocaleString() : '—'}
              {p.last_story_count != null && p.last_story_count > 0 && (
                <span style={{ fontSize: 'var(--fs-micro)', color: '#778', marginLeft: 4 }}>
                  (+{p.last_story_count})
                </span>
              )}
            </div>

            {/* Last story scrape */}
            <div style={{ textAlign: 'right', color: '#9aa', fontSize: 'var(--fs-small)' }}>
              {fmtDate(p.last_story_scrape_at) ?? <span style={{ color: '#666' }}>{t('providers.scrape.never', { defaultValue: 'never' })}</span>}
            </div>

            {/* Scrape + Mirror buttons */}
            {(() => {
              const isScraping = scraping === saId;
              const handle = p.handle?.replace(/^@+/, '') ?? null;
              return (
                <div style={{ textAlign: 'right', display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    disabled={isScraping || !handle || !saId}
                    title="Scrape posts + reels from Apify"
                    onClick={() => handle && saId && triggerScrape(handle, 'both', saId)}
                    style={{
                      padding: '3px 8px', fontSize: 'var(--fs-caption)', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit',
                      background: isScraping ? 'rgba(95,209,122,0.1)' : 'rgba(74,158,255,0.12)',
                      color: isScraping ? '#5fd17a' : '#bcd0ff',
                      border: `1px solid ${isScraping ? 'rgba(95,209,122,0.3)' : 'rgba(74,158,255,0.3)'}`,
                      opacity: !handle || !saId ? 0.4 : 1,
                    }}
                  >
                    {isScraping ? '⟳ …' : '📸 Posts'}
                  </button>
                </div>
              );
            })()}

            {/* Actions */}
            {deletable && (
              <div style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteRequest(p); }}
                  disabled={isDeleting}
                  aria-label={t('providers.delete.button', { defaultValue: 'Remove' })}
                  title={t('providers.delete.tooltip', { defaultValue: 'Remove from list (data preserved)' })}
                  style={{
                    padding: '4px 10px',
                    background: 'transparent',
                    color: '#e74c3c',
                    border: '1px solid rgba(231,76,60,0.4)',
                    borderRadius: 6,
                    cursor: isDeleting ? 'wait' : 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 'var(--fs-caption)',
                  }}
                >
                  {isDeleting ? '…' : t('providers.delete.button', { defaultValue: 'Remove' })}
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Footer totals */}
      <div style={{
        display: 'grid', gridTemplateColumns: gridTemplate,
        padding: '12px 14px', gap: 12, alignItems: 'center',
        borderTop: '2px solid rgba(74,158,255,0.4)',
        background: 'rgba(15,45,100,0.18)',
        fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
      }}>
        <div style={{ color: '#bcd0ff' }}>
          {t('providers.totals.label', { defaultValue: 'Totals' })}
          <span style={{ marginLeft: 6, fontSize: 'var(--fs-caption)', color: '#9aa', fontWeight: 'var(--fw-regular)' }}>
            ({rows.length} {t('providers.totals.creators', { defaultValue: 'creators' })})
          </span>
        </div>
        <div style={{ textAlign: 'right', color: '#bcd0ff' }}>{totals.followers.toLocaleString()}</div>
        <div style={{ textAlign: 'right', color: '#bcd0ff' }}>{erAvg != null ? `${erAvg.toFixed(2)}%` : '—'}</div>
        <div style={{ textAlign: 'right', color: '#bcd0ff' }}>{totals.posts.toLocaleString()}</div>
        <div style={{ textAlign: 'right', color: '#778' }}>—</div>
        <div style={{ textAlign: 'right', color: '#778' }}>—</div>
        <div style={{ textAlign: 'right', color: '#bcd0ff' }}>{totals.stories.toLocaleString()}</div>
        <div style={{ textAlign: 'right', color: '#778' }}>—</div>
        <div />
        {deletable && <div />}
      </div>

      {deleteTarget && (
        <DeleteCreatorDialog
          provider={deleteTarget}
          onClose={onDeleteClose}
          onDone={onDeleteDone}
        />
      )}
    </div>
    </div>
  );
}

interface DeletePreview {
  handle: string | null;
  instagram_provider_id: string | null;
  posts: number;
  stories: number;
  snapshots: number;
  post_mentions: number;
  story_mentions: number;
}

function DeleteCreatorDialog({
  provider, onClose, onDone,
}: {
  provider: ProviderRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('admin');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState<'soft' | 'hard' | null>(null);

  const previewQ = useSupabaseQuery<DeletePreview>(
    ['admin-creator-delete-preview', provider.id],
    async () => supabase.rpc('admin_ig_creator_delete_preview', { p_provider_id: provider.id }),
  );
  const preview = previewQ.data ?? null;

  useCloseOnTabSwitch(onClose, !busy);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const total = preview
    ? preview.posts + preview.stories + preview.snapshots + preview.post_mentions + preview.story_mentions
    : 0;
  const hasData = total > 0;

  const doSoft = async () => {
    setBusy('soft');
    try {
      const { error: err } = await supabase.rpc('admin_delete_ig_creator', { p_provider_id: provider.id });
      if (err) { alert(err.message); return; }
      onDone();
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  const doHard = async () => {
    if (hasData && !acknowledged) return;
    setBusy('hard');
    try {
      const { error: err } = await supabase.rpc('admin_hard_delete_ig_creator', { p_provider_id: provider.id });
      if (err) { alert(err.message); return; }
      onDone();
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  };

  const label = provider.handle || provider.name || provider.id;

  return (
    <div
      role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '8vh 16px', backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, background: 'var(--bg-elev, #161b22)',
          border: '1px solid rgba(15,45,100,0.5)', borderRadius: 12, padding: 22,
        }}
      >
        <h3 style={{ margin: '0 0 6px', fontSize: 'var(--fs-label)' }}>
          {t('providers.delete.title', { defaultValue: 'Remove {{label}}?', label })}
        </h3>
        <div style={{ fontSize: 'var(--fs-body)', color: '#9aa', marginBottom: 16 }}>
          {previewQ.isLoading
            ? t('common.loading', { defaultValue: 'Loading…' })
            : !hasData
              ? t('providers.delete.noData', { defaultValue: 'No scraped data attached. Soft and hard delete are equivalent here.' })
              : t('providers.delete.summary', {
                  defaultValue: 'Attached: {{posts}} posts · {{stories}} stories · {{post_mentions}} post mentions · {{story_mentions}} story mentions · {{snapshots}} profile snapshots.',
                  posts: preview!.posts, stories: preview!.stories,
                  post_mentions: preview!.post_mentions, story_mentions: preview!.story_mentions,
                  snapshots: preview!.snapshots,
                })}
        </div>

        {/* Soft delete */}
        <div style={{ padding: 14, borderRadius: 8, border: '1px solid rgba(15,45,100,0.5)', marginBottom: 10 }}>
          <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', marginBottom: 4 }}>
            {t('providers.delete.softTitle', { defaultValue: 'Remove from list (recommended)' })}
          </div>
          <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginBottom: 10 }}>
            {t('providers.delete.softDesc', { defaultValue: 'Stops the scraper from touching this creator. All scraped posts and brand mentions stay in the DB. Re-adding the same handle later restores everything.' })}
          </div>
          <button
            type="button" onClick={doSoft} disabled={!!busy}
            style={{
              padding: '8px 14px', background: '#4a9eff', color: '#fff', border: 'none',
              borderRadius: 6, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-body)', opacity: busy === 'hard' ? 0.4 : 1,
            }}
          >
            {busy === 'soft' ? '…' : t('providers.delete.softAction', { defaultValue: 'Remove from list' })}
          </button>
        </div>

        {/* Hard delete */}
        <div style={{ padding: 14, borderRadius: 8, border: '1px solid rgba(231,76,60,0.4)', marginBottom: 16, background: 'rgba(231,76,60,0.05)' }}>
          <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', marginBottom: 4, color: '#e74c3c' }}>
            {t('providers.delete.hardTitle', { defaultValue: 'Delete everything' })}
          </div>
          <div style={{ fontSize: 'var(--fs-small)', color: '#bbc', marginBottom: 10 }}>
            {t('providers.delete.hardDesc', { defaultValue: 'Permanently deletes the creator and every scraped row attached to them (posts, stories, snapshots, brand mentions). Cannot be undone.' })}
          </div>
          {hasData && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, fontSize: 'var(--fs-small)', color: '#bbc' }}>
              <input
                type="checkbox" checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={!!busy}
              />
              {t('providers.delete.hardConfirm', { defaultValue: 'I understand this destroys {{count}} scraped rows.', count: total })}
            </label>
          )}
          <button
            type="button" onClick={doHard}
            disabled={!!busy || (hasData && !acknowledged)}
            style={{
              padding: '8px 14px',
              background: hasData && !acknowledged ? 'transparent' : '#e74c3c',
              color: hasData && !acknowledged ? '#9aa' : '#fff',
              border: hasData && !acknowledged ? '1px solid rgba(231,76,60,0.4)' : 'none',
              borderRadius: 6,
              cursor: busy || (hasData && !acknowledged) ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-body)',
              opacity: busy === 'soft' ? 0.4 : 1,
            }}
          >
            {busy === 'hard' ? '…' : t('providers.delete.hardAction', { defaultValue: 'Delete everything' })}
          </button>
        </div>

        <div style={{ textAlign: 'right' }}>
          <button
            type="button" onClick={onClose} disabled={!!busy}
            style={{
              padding: '6px 12px', background: 'transparent', color: '#9aa',
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
              cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-body)',
            }}
          >
            {t('providers.delete.cancel', { defaultValue: 'Cancel' })}
          </button>
        </div>
      </div>
    </div>
  );
}


interface SuggestedRow {
  handle: string;
  mention_count: number;
  usertag_count: number;
  total: number;
  distinct_bloggers: number;
  classified_kind: 'brand' | 'blogger' | 'unknown' | null;
  classified_confidence: number | null;
  classified_manually: boolean | null;
  niche: string | null;                          // legacy single niche (kept for compat)
  classify_niche_ids: string[] | null;           // canonical niche_ids array
  classify_niche_names: string[] | null;         // resolved Russian names parallel to ids
  suggested_display_name: string | null;
  um_follower_count: number | null;              // Apify-enriched IG follower count
  um_brand_tag_count: number | null;             // # known brands tagged in recent posts
  um_brand_tag_brands: string[] | null;          // names of those brands
  um_ambassador_handles: string[] | null;        // @handles they declare ambassadorship for (bio)
  um_is_verified: boolean | null;                // scraped IG blue check
  um_classify_country: string | null;            // detected country
}

// Columns the suggested table can sort by (must match p_sort keys in get_recommended_creators_filtered).
type SuggestedSortKey = 'handle' | 'mention_count' | 'distinct_bloggers' | 'follower_count' | 'classify_country';

// compact follower count: 1234 -> "1.2K", 1200000 -> "1.2M"
function fmtFollowers(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

type SuggestedFilter = 'all' | 'ai_blogger' | 'unclassified' | 'brand_tagged' | 'ambassador';

function SuggestedCreatorsList({ search, tagged, bioKw }: { search: string; tagged: string; bioKw: string }) {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const [adding, setAdding] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [filter, setFilter] = useState<SuggestedFilter>('all');
  const [minFollowers, setMinFollowers] = useState('');
  const [maxFollowers, setMaxFollowers] = useState('');
  const [country, setCountry] = useState('');
  // Column sort (server-side via RPC p_sort/p_dir). null ⇒ default rank order.
  const [sortKey, setSortKey] = useState<SuggestedSortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSort = (key: string) => {
    if (sortKey === key) {
      // toggle desc→asc, then a third click clears back to default rank
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortKey(null); setSortDir('desc'); }
    } else { setSortKey(key as SuggestedSortKey); setSortDir('desc'); }
    setPage(0);
  };
  const [cardHandle, setCardHandle] = useState<string | null>(null);
  const [tagPostsHandle, setTagPostsHandle] = useState<string | null>(null);
  // tagged + bioKw are lifted to the parent (rendered in the shared search row).

  // Debounce searches so we don't refire on every keystroke
  const debouncedSearch = useDebounce(search, 300);
  const debouncedTagged = useDebounce(tagged, 300);
  const debouncedBio = useDebounce(bioKw, 300);
  const debouncedMin = useDebounce(minFollowers, 300);
  const debouncedMax = useDebounce(maxFollowers, 300);
  // Parse the digit-only follower inputs to a number | null for the RPC params.
  const minF = debouncedMin.trim() === '' ? null : (parseInt(debouncedMin, 10) || null);
  const maxF = debouncedMax.trim() === '' ? null : (parseInt(debouncedMax, 10) || null);
  // Reset to page 0 whenever any search, the filter, the follower range, or country changes
  useEffect(() => { setPage(0); }, [debouncedSearch, debouncedTagged, debouncedBio, minF, maxF, country, filter]);
  const countryParam = country || null;

  const { data, isLoading, error } = useSupabaseQuery<SuggestedRow[]>(
    ['admin-recommended-creators', filter, page, pageSize, debouncedSearch, debouncedTagged, debouncedBio, minF, maxF, countryParam, sortKey, sortDir],
    async () => supabase.rpc('get_recommended_creators_filtered', {
      p_filter: filter,
      p_limit: pageSize,
      p_offset: page * pageSize,
      p_search: debouncedSearch || null,
      p_tagged: debouncedTagged || null,
      p_bio: debouncedBio || null,
      p_min_followers: minF,
      p_max_followers: maxF,
      p_country: countryParam,
      p_sort: sortKey,
      p_dir: sortDir,
    }),
  );
  // Breakdown for chip counts + total — also search-aware
  const { data: breakdown } = useSupabaseQuery<{ all_count: number; ai_blogger_count: number; unclassified_count: number; brand_tagged_count: number; ambassador_count: number }[]>(
    ['admin-recommended-creators-breakdown', debouncedSearch, debouncedTagged, debouncedBio, minF, maxF, countryParam],
    async () => supabase.rpc('get_recommended_creators_breakdown', {
      p_search: debouncedSearch || null,
      p_tagged: debouncedTagged || null,
      p_bio: debouncedBio || null,
      p_min_followers: minF,
      p_max_followers: maxF,
      p_country: countryParam,
    }),
  );
  // Country list for the dropdown (respects search + follower range, not the country itself)
  const { data: countryList } = useSupabaseQuery<{ country: string; n: number }[]>(
    ['admin-recommended-creators-countries', debouncedSearch, debouncedTagged, debouncedBio, minF, maxF],
    async () => supabase.rpc('get_recommended_creators_countries', {
      p_search: debouncedSearch || null,
      p_tagged: debouncedTagged || null,
      p_bio: debouncedBio || null,
      p_min_followers: minF,
      p_max_followers: maxF,
    }),
  );
  const counts = breakdown?.[0] ?? { all_count: 0, ai_blogger_count: 0, unclassified_count: 0, brand_tagged_count: 0, ambassador_count: 0 };
  const total =
    filter === 'ai_blogger'   ? counts.ai_blogger_count :
    filter === 'unclassified' ? counts.unclassified_count :
    filter === 'brand_tagged' ? counts.brand_tagged_count :
    filter === 'ambassador'   ? counts.ambassador_count :
    counts.all_count;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // NOTE: the "AI Classify all" bulk button was removed (2026-06-26). It looped
  // the whole recommended-creator queue through the Haiku classify-handle Edge
  // Function; one accidental click could burn the entire API budget (~$7 incident).
  // Classification is now done out-of-band; the admin only links/skips rows here.

  const onMoveToBrand = async (handle: string) => {
    const { error: err } = await supabase
      .from('unknown_mentions')
      .upsert({
        handle,
        classify_kind: 'brand',
        classify_confidence: 1.0,
        classify_reasoning: 'Manual override (admin moved from Suggested to Brands).',
        classify_ran_at: new Date().toISOString(),
        suggested_status: 'brand',
      }, { onConflict: 'handle' });
    if (err) { alert(err.message); return; }
    qc.invalidateQueries({ queryKey: ['admin-recommended-creators'] });
          qc.invalidateQueries({ queryKey: ['admin-recommended-creators-breakdown'] });
    qc.invalidateQueries({ queryKey: ['admin-pending-brand-handles'] });
  };

  const onSkip = async (handle: string) => {
    const { error: err } = await supabase.rpc('admin_skip_handle_as_creator', {
      p_handle: handle,
      p_reason: null,
    });
    if (err) { alert(err.message); return; }
    qc.invalidateQueries({ queryKey: ['admin-recommended-creators'] });
          qc.invalidateQueries({ queryKey: ['admin-recommended-creators-breakdown'] });
  };

  // Search is server-side now via p_search → no client filter needed
  const rows = data ?? [];

  const onAdd = async (handle: string, suggestedName?: string | null) => {
    setAdding(handle);
    try {
      const { data: res, error: err } = await supabase.rpc('admin_create_ig_creator', {
        p_handle: handle,
        p_name: suggestedName?.trim() || null,
      });
      if (err) {
        alert(err.message);
        return;
      }
      const created = Array.isArray(res) ? res[0]?.created : (res as { created?: boolean } | null)?.created;
      // created === false means "already in instagram_providers" (probably orphan from
      // a past mention scrape). Either way the row is now usable, so mark it added.
      setAdded((prev) => new Set(prev).add(handle));
      if (created !== false) {
        qc.invalidateQueries({ queryKey: ['admin-providers'] });
        qc.invalidateQueries({ queryKey: ['admin-recommended-creators'] });
          qc.invalidateQueries({ queryKey: ['admin-recommended-creators-breakdown'] });
      }
    } catch (e) {
      alert((e as Error)?.message ?? String(e));
    } finally {
      setAdding(null);
    }
  };

  if (isLoading) return <Skeleton height={240} />;
  if (error) return <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>;
  if (rows.length === 0) {
    return <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>{t('providers.suggested.empty', { defaultValue: 'No new candidates right now.' })}</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 'var(--fs-small)', color: '#9aa', flex: 1, minWidth: 240 }}>
          {t('providers.suggested.help', {
            defaultValue: 'Handles seen in scraped post @mentions and usertags but not yet onboarded. Ranked by total appearances; “bloggers” = how many distinct existing creators referenced them.',
          })}
        </div>
      </div>

      {/* Followers-count range filter (server-side via p_min_followers / p_max_followers) */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-small)', color: '#9aa' }}>
          {t('providers.suggested.followersLabel', { defaultValue: '👥 Followers' })}
        </span>
        <input
          type="text"
          inputMode="numeric"
          placeholder={t('providers.suggested.followersMin', { defaultValue: 'min' })}
          value={minFollowers}
          onChange={(e) => setMinFollowers(e.target.value.replace(/\D/g, ''))}
          style={{ width: 90, padding: '5px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            background: 'transparent', color: '#cde', border: '1px solid rgba(255,255,255,0.15)' }}
        />
        <span style={{ color: '#9aa' }}>–</span>
        <input
          type="text"
          inputMode="numeric"
          placeholder={t('providers.suggested.followersMax', { defaultValue: 'max' })}
          value={maxFollowers}
          onChange={(e) => setMaxFollowers(e.target.value.replace(/\D/g, ''))}
          style={{ width: 90, padding: '5px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            background: 'transparent', color: '#cde', border: '1px solid rgba(255,255,255,0.15)' }}
        />
        {[1000, 5000, 10000, 50000].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setMinFollowers(String(v))}
            style={{ padding: '4px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', cursor: 'pointer', fontFamily: 'inherit',
              background: minF === v ? 'rgba(74,158,255,0.2)' : 'transparent',
              color: minF === v ? '#4a9eff' : '#9aa',
              border: `1px solid ${minF === v ? '#4a9eff' : 'rgba(255,255,255,0.15)'}` }}
          >{v >= 1000 ? `${v / 1000}k+` : `${v}+`}</button>
        ))}
        {(minFollowers || maxFollowers) && (
          <button
            type="button"
            onClick={() => { setMinFollowers(''); setMaxFollowers(''); }}
            style={{ padding: '4px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', cursor: 'pointer', fontFamily: 'inherit',
              background: 'transparent', color: '#9aa', border: '1px solid rgba(255,255,255,0.15)' }}
          >{t('providers.suggested.followersClear', { defaultValue: '✕ clear' })}</button>
        )}
        {/* Country filter (classify_country); '__NONE__' = unclassified */}
        <span style={{ fontSize: 'var(--fs-small)', color: '#9aa', marginLeft: 8 }}>
          {t('providers.suggested.countryLabel', { defaultValue: '🌍 Country' })}
        </span>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          style={{ padding: '5px 8px', borderRadius: 6, fontSize: 'var(--fs-small)', fontFamily: 'inherit',
            background: 'transparent', color: '#cde', border: `1px solid ${country ? '#4a9eff' : 'rgba(255,255,255,0.15)'}` }}
        >
          <option value="" style={{ background: '#1a1f2b' }}>
            {t('providers.suggested.countryAll', { defaultValue: 'All countries' })}
          </option>
          {(countryList ?? []).map((c) => (
            <option key={c.country} value={c.country} style={{ background: '#1a1f2b' }}>
              {(c.country === '__NONE__'
                ? t('providers.suggested.countryNone', { defaultValue: 'Unclassified' })
                : c.country)} · {c.n.toLocaleString()}
            </option>
          ))}
        </select>
      </div>

      {/* Filter chips: All / AI-blogger / Unclassified */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {([
          { key: 'all',          label: 'All',                count: counts.all_count,           color: '#9aa' },
          { key: 'ambassador',   label: '🎀 Ambassadors',     count: counts.ambassador_count,    color: '#9B5DE5' },
          { key: 'brand_tagged', label: '🏷 Tagged brands',   count: counts.brand_tagged_count,  color: '#4a9eff' },
          { key: 'ai_blogger',   label: '🤖 AI-blogger',      count: counts.ai_blogger_count,    color: '#2ecc71' },
          { key: 'unclassified', label: '❓ Needs attention', count: counts.unclassified_count,  color: '#f5b700' },
        ] as const).map((c) => {
          const active = filter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => { setFilter(c.key); setPage(0); }}
              style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)',
                cursor: 'pointer', fontFamily: 'inherit',
                background: active ? c.color + '33' : 'transparent',
                color: active ? c.color : '#9aa',
                border: `1px solid ${active ? c.color : 'rgba(255,255,255,0.15)'}`,
              }}
            >{c.label} · {c.count.toLocaleString()}</button>
          );
        })}
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} total={total} totalPages={totalPages}
        loaded={(data ?? []).length}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        onPageSize={(n) => { setPageSize(n); setPage(0); }}
      />

      <div style={{ overflowX: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-small)', minWidth: 940 }}>
          <thead>
            <tr style={{ background: 'rgba(15,45,100,0.18)' }}>
              <SortHeader label={t('providers.brands.colHandle', { defaultValue: 'Handle' })}
                col="handle" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="left" />
              <SortHeader label={t('providers.suggested.colMentions', { defaultValue: 'Mentions' })}
                col="mention_count" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortHeader label={t('providers.brands.colBloggers', { defaultValue: 'Bloggers' })}
                col="distinct_bloggers" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortHeader label={t('providers.brands.colFollowers', { defaultValue: 'Followers' })}
                col="follower_count" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
              <SortHeader label={t('providers.brands.colCountry', { defaultValue: 'Country' })}
                col="classify_country" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="left" />
              <th style={{ ...THStyle, textAlign: 'left', cursor: 'default' }}>
                {t('providers.brands.colSignals', { defaultValue: 'Signals' })}
              </th>
              <th style={{ ...THStyle, textAlign: 'right', cursor: 'default' }}>
                {t('providers.brands.colActions', { defaultValue: 'Actions' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isAdded = added.has(r.handle);
              const isBusy = adding === r.handle;
              return (
                <tr key={r.handle} style={{ borderTop: '1px solid rgba(15,45,100,0.3)', opacity: isAdded ? 0.5 : 1 }}>
                  {/* Handle */}
                  <td style={{ ...TDStyle, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setCardHandle(r.handle)}
                        title={t('providers.suggested.openCard', { defaultValue: 'View card' })}
                        style={{
                          padding: 0, background: 'transparent', border: 'none',
                          color: '#bcd0ff', fontFamily: 'inherit', fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)',
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >@{r.handle}</button>
                      <VerifiedBadge verified={r.um_is_verified} />
                      <a
                        href={`https://www.instagram.com/${r.handle}/`}
                        target="_blank" rel="noopener noreferrer"
                        title={t('providers.suggested.openProfile', { defaultValue: 'Open on Instagram' })}
                        aria-label="Open on Instagram"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 18, height: 18, borderRadius: 4, color: '#bcd0ff', opacity: 0.6,
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                        </svg>
                      </a>
                    </div>
                    {(r.um_ambassador_handles ?? []).length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {(r.um_ambassador_handles ?? []).map((h) => (
                          <a
                            key={h}
                            href={`https://www.instagram.com/${h}/`}
                            target="_blank" rel="noopener noreferrer"
                            title="Declared brand ambassador (from bio)"
                            style={{
                              fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '1px 6px', borderRadius: 4, textDecoration: 'none',
                              background: 'rgba(155,93,229,0.15)', color: '#b18cf0', border: '1px solid rgba(155,93,229,0.45)',
                            }}
                          >🎀 @{h}</a>
                        ))}
                      </div>
                    )}
                  </td>
                  {/* Mentions */}
                  <td style={{ ...TDStyle, textAlign: 'right', color: '#cde', fontVariantNumeric: 'tabular-nums' }}
                    title={t('providers.suggested.mentionsTip', { defaultValue: 'Times mentioned across collected posts' })}>
                    {r.mention_count.toLocaleString()}
                  </td>
                  {/* Bloggers */}
                  <td style={{ ...TDStyle, textAlign: 'right', color: '#cde', fontVariantNumeric: 'tabular-nums' }}>
                    {r.distinct_bloggers.toLocaleString()}
                  </td>
                  {/* Followers */}
                  <td style={{ ...TDStyle, textAlign: 'right', color: r.um_follower_count != null ? '#2ecc71' : '#556', fontVariantNumeric: 'tabular-nums', fontWeight: r.um_follower_count != null ? 'var(--fw-semibold)' : undefined }}>
                    {r.um_follower_count != null ? fmtFollowers(r.um_follower_count) : '—'}
                  </td>
                  {/* Country */}
                  <td style={{ ...TDStyle, color: r.um_classify_country ? '#cde' : '#556', whiteSpace: 'nowrap' }}>
                    {r.um_classify_country ?? '—'}
                  </td>
                  {/* Signals */}
                  <td style={{ ...TDStyle }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {r.um_brand_tag_count != null && r.um_brand_tag_count > 0 && (
                        <button
                          type="button"
                          onClick={() => setTagPostsHandle(r.handle)}
                          title={`${(r.um_brand_tag_brands ?? []).join(', ')} — click to see the posts`}
                          style={{
                            padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', cursor: 'pointer', fontFamily: 'inherit',
                            background: 'rgba(74,158,255,0.15)', color: '#4a9eff', border: '1px solid rgba(74,158,255,0.4)',
                          }}
                        >🏷 {r.um_brand_tag_count}</button>
                      )}
                      {r.classified_kind && (
                        <span
                          title={r.classified_manually
                            ? 'Manually classified by admin'
                            : `AI confidence ${Math.round((r.classified_confidence ?? 0) * 100)}%`}
                          style={{
                            padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-semibold)',
                            background: r.classified_kind === 'brand' ? 'rgba(74,158,255,0.18)' : r.classified_kind === 'blogger' ? 'rgba(46,204,113,0.18)' : 'rgba(155,93,229,0.15)',
                            color: r.classified_kind === 'brand' ? '#4a9eff' : r.classified_kind === 'blogger' ? '#2ecc71' : '#9B5DE5',
                            border: '1px solid currentColor',
                          }}
                        >
                          {r.classified_manually ? '✓ ' : '🤖 '}{r.classified_kind}
                          {!r.classified_manually && r.classified_confidence != null && (
                            <> · {Math.round(r.classified_confidence * 100)}%</>
                          )}
                        </span>
                      )}
                      {/* Niche chips: prefer canonical niche_names array, fall back to legacy single string */}
                      {(() => {
                        const names = r.classify_niche_names && r.classify_niche_names.length > 0
                          ? r.classify_niche_names
                          : r.niche
                            ? [r.niche]
                            : [];
                        if (names.length === 0) return null;
                        return names.map((n) => (
                          <span
                            key={n}
                            title="AI niche tag (will be copied to instagram_providers.niche_ids on Add)"
                            style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: 'var(--fs-micro)',
                              background: 'rgba(155,93,229,0.10)', color: '#b18cf0', border: '1px solid rgba(155,93,229,0.4)',
                            }}
                          >🎯 {n}</span>
                        ));
                      })()}
                      {r.suggested_display_name && r.suggested_display_name !== r.handle && (
                        <span
                          title={`AI display name suggestion — used as the name when you click Add`}
                          style={{ fontSize: 'var(--fs-micro)', color: '#bdd', fontStyle: 'italic' }}
                        >
                          name: {r.suggested_display_name}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Actions */}
                  <td style={{ ...TDStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => onAdd(r.handle, r.suggested_display_name)}
                        disabled={isBusy || isAdded}
                        style={{
                          padding: '5px 10px',
                          background: isAdded ? 'transparent' : '#4a9eff',
                          color: isAdded ? '#9aa' : '#fff',
                          border: isAdded ? '1px solid rgba(255,255,255,0.15)' : 'none',
                          borderRadius: 6,
                          cursor: isBusy || isAdded ? 'default' : 'pointer',
                          fontFamily: 'inherit', fontSize: 'var(--fs-small)', fontWeight: 'var(--fw-semibold)',
                        }}
                      >
                        {isAdded
                          ? t('providers.suggested.added', { defaultValue: 'Added' })
                          : isBusy ? '…' : t('providers.suggested.add', { defaultValue: 'Add' })}
                      </button>
                      <button
                        type="button"
                        onClick={() => onMoveToBrand(r.handle)}
                        disabled={isBusy || isAdded}
                        title="This handle is actually a brand — move to Brands tab"
                        style={{
                          padding: '5px 8px', background: 'transparent', color: '#4a9eff',
                          border: '1px solid rgba(74,158,255,0.4)', borderRadius: 6,
                          cursor: isBusy || isAdded ? 'default' : 'pointer',
                          fontFamily: 'inherit', fontSize: 'var(--fs-small)',
                        }}
                      >
                        → brand
                      </button>
                      <button
                        type="button"
                        onClick={() => onSkip(r.handle)}
                        disabled={isBusy || isAdded}
                        title="Hide this handle from recommendations (restore from the Skipped tab in Brands review)"
                        style={{
                          padding: '5px 8px', background: 'transparent', color: '#9aa',
                          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                          cursor: isBusy || isAdded ? 'default' : 'pointer',
                          fontFamily: 'inherit', fontSize: 'var(--fs-small)',
                        }}
                      >
                        Skip
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {tagPostsHandle && (
        <BrandTagPostsModal handle={tagPostsHandle} onClose={() => setTagPostsHandle(null)} />
      )}
      {cardHandle && (
        <SuggestedHandleCard handle={cardHandle} onClose={() => setCardHandle(null)} />
      )}
    </>
  );
}

function AddCreatorButton() {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const qc = useQueryClient();

  const close = () => { setOpen(false); setHandle(''); setName(''); setMsg(null); };

  useCloseOnTabSwitch(close);

  const submit = async () => {
    setSubmitting(true);
    setMsg(null);
    try {
      const { data, error } = await supabase.rpc('admin_create_ig_creator', {
        p_handle: handle.trim(),
        p_name: name.trim() || null,
      });
      if (error) {
        setMsg({ kind: 'err', text: error.message });
        return;
      }
      const created = Array.isArray(data) ? data[0]?.created : (data as { created?: boolean } | null)?.created;
      if (created === false) {
        setMsg({ kind: 'err', text: t('providers.add.duplicate') });
        return;
      }
      setMsg({ kind: 'ok', text: t('providers.add.success') });
      qc.invalidateQueries({ queryKey: ['admin-providers'] });
      setHandle('');
      setName('');
    } catch (e) {
      // Network/transport errors don't come through Supabase's `error` channel;
      // they reject the promise. Without try/finally the button used to be stuck
      // on its loading "…" state forever.
      setMsg({ kind: 'err', text: (e as Error)?.message ?? String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          padding: '8px 14px',
          background: '#4a9eff',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 'var(--fs-body)',
          fontWeight: 'var(--fw-semibold)',
        }}
      >
        {t('providers.add.button')}
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={close}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '10vh 16px', backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, background: 'var(--bg-elev, #161b22)',
          border: '1px solid rgba(15,45,100,0.5)', borderRadius: 12, padding: 22,
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: 'var(--fs-label)' }}>{t('providers.add.title')}</h3>

        <label style={{ display: 'block', fontSize: 'var(--fs-small)', color: '#9aa', marginBottom: 4 }}>
          {t('providers.add.handle')}
        </label>
        <Input
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder={t('providers.add.handleHint')}
          autoFocus
          style={{ width: '100%', marginBottom: 12 }}
        />

        <label style={{ display: 'block', fontSize: 'var(--fs-small)', color: '#9aa', marginBottom: 4 }}>
          {t('providers.add.name')}
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: '100%', marginBottom: 16 }}
        />

        {msg && (
          <div style={{
            padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 'var(--fs-body)',
            background: msg.kind === 'ok' ? 'rgba(46,204,113,0.12)' : 'rgba(231,76,60,0.12)',
            color: msg.kind === 'ok' ? '#2ecc71' : '#e74c3c',
            border: `1px solid ${msg.kind === 'ok' ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)'}`,
          }}>
            {msg.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={close}
            style={{ padding: '8px 14px', background: 'transparent', color: '#dde', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {t('providers.add.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !handle.trim()}
            style={{
              padding: '8px 14px', background: '#4a9eff', color: '#fff', border: 'none',
              borderRadius: 6, cursor: submitting || !handle.trim() ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', fontWeight: 'var(--fw-semibold)',
              opacity: submitting || !handle.trim() ? 0.55 : 1,
            }}
          >
            {submitting ? '…' : t('providers.add.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

